import React, { useState, useEffect } from 'react';
import { adminTOTPAPI } from '../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  QrCode2 as QrIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

const SetupTOTP = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Setup state
  const [qrCode, setQrCode] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  // Backup codes (shown once after enable)
  const [backupCodes, setBackupCodes] = useState(null);

  // Disable state
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await adminTOTPAPI.getStatus();
      setStatus(response.data);
    } catch (err) {
      setError('Failed to load TOTP status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSetup = async () => {
    try {
      setSetupLoading(true);
      setError('');
      const response = await adminTOTPAPI.setup();
      setQrCode(response.data.qrCode);
      setManualSecret(response.data.manualSecret);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup TOTP');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    try {
      setSetupLoading(true);
      setError('');
      const response = await adminTOTPAPI.enable(setupCode);
      setBackupCodes(response.data.backupCodes);
      setSuccess('Two-factor authentication enabled!');
      setQrCode('');
      setManualSecret('');
      setSetupCode('');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enable TOTP');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    try {
      setDisableLoading(true);
      setError('');
      await adminTOTPAPI.disable(disablePassword, disableCode);
      setSuccess('Two-factor authentication disabled');
      setDisablePassword('');
      setDisableCode('');
      setBackupCodes(null);
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable TOTP');
    } finally {
      setDisableLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SecurityIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
          <Typography variant="h5" fontWeight={600}>
            Two-Factor Authentication
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Current Status */}
        <Box sx={{ mb: 3 }}>
          <Chip
            icon={status?.enabled ? <CheckIcon /> : <SecurityIcon />}
            label={status?.enabled ? 'Enabled' : 'Disabled'}
            color={status?.enabled ? 'success' : 'default'}
            variant="outlined"
            sx={{ mr: 1 }}
          />
          {status?.enabled && status?.backupCodesRemaining !== undefined && (
            <Chip
              label={`${status.backupCodesRemaining} backup codes remaining`}
              color={status.backupCodesRemaining <= 2 ? 'warning' : 'default'}
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Backup codes display (shown once after enabling) */}
        {backupCodes && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Save these backup codes! They will not be shown again.
            </Typography>
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                bgcolor: '#fff',
                p: 2,
                borderRadius: 1,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 0.5,
              }}
            >
              {backupCodes.map((code, i) => (
                <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {code}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}

        {!status?.enabled ? (
          <>
            {/* Setup flow */}
            {!qrCode ? (
              <Button
                variant="contained"
                startIcon={<QrIcon />}
                onClick={handleSetup}
                disabled={setupLoading}
              >
                {setupLoading ? <CircularProgress size={20} /> : 'Set up 2FA'}
              </Button>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
                </Typography>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img src={qrCode} alt="TOTP QR Code" style={{ width: 200, height: 200 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Or enter this code manually:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    wordBreak: 'break-all',
                    mb: 3,
                  }}
                >
                  {manualSecret}
                </Typography>

                <Box component="form" onSubmit={handleEnable}>
                  <TextField
                    fullWidth
                    label="Enter 6-digit code from your app"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value)}
                    inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
                    required
                    sx={{ mb: 2 }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={setupLoading || setupCode.length !== 6}
                    fullWidth
                  >
                    {setupLoading ? <CircularProgress size={20} /> : 'Enable 2FA'}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Disable flow */}
            <Typography variant="body2" sx={{ mb: 2 }}>
              To disable two-factor authentication, enter your password and a current TOTP code.
            </Typography>
            <Box component="form" onSubmit={handleDisable}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="TOTP Code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
                required
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                variant="outlined"
                color="error"
                disabled={disableLoading || !disablePassword || disableCode.length !== 6}
                fullWidth
              >
                {disableLoading ? <CircularProgress size={20} /> : 'Disable 2FA'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SetupTOTP;
